import type { ThemeColor } from "@/colors/index";
import type { FlexComponent } from "@/utils/render-component";
import type { RequiredOrOptional } from "@/utils/types";
import { computed, defineComponent, nextTick, onUnmounted, ref, watch } from "vue";
import { $t } from "@/services/i18n";
import { renderComponent } from "@/utils/render-component";
import AlertDialog from "../primitives/alert-dialog/AlertDialog";
import Button from "../ui/button/Button";
import Input from "../ui/input/Input";

type AlertModalSize = "sm" | "md" | "lg" | "xl";

const activeModal = ref<FlexComponent | null>(null);
const size = ref<AlertModalSize>("lg");

let focusedElementBeforeModal: Element | null = null;

export function openAlertDialog(
  component: FlexComponent,
  options?: {
    size?: AlertModalSize;
  }
) {
  size.value = options?.size ?? "lg";
  if (activeModal.value) throw new Error("Modal already open");

  focusedElementBeforeModal = document.activeElement;

  activeModal.value = component;
}

export function closeAlertDialog() {
  activeModal.value = null;

  if (focusedElementBeforeModal) {
    nextTick(() => {
      (focusedElementBeforeModal as HTMLElement)?.focus();
      focusedElementBeforeModal = null;
    });
  }
}

export default defineComponent({
  name: "AlertDialogProvider",
  setup() {
    const open = computed({
      get: () => !!activeModal.value,
      set: (value) => {
        if (!value) closeAlertDialog();
      },
    });

    watch(activeModal, (value) => {
      if (value) open.value = true;
      else open.value = false;
    });

    onUnmounted(() => {
      closeAlertDialog();
    });

    const sizeClass = computed(() => {
      switch (size.value) {
        case "sm":
          return "max-w-sm";
        case "md":
          return "max-w-md";
        case "lg":
          return "max-w-lg";
        case "xl":
          return "max-w-xl";
      }
    });

    return () => (
      <AlertDialog.Root v-model:open={open.value}>
        <AlertDialog.Content
          class={sizeClass.value}
          onCloseAutoFocus={() => {
            open.value = false;
          }}
        >
          {activeModal.value ? renderComponent(activeModal.value) : null}
        </AlertDialog.Content>
      </AlertDialog.Root>
    );
  },
});

interface ConfirmPromptOptions<Phrase extends string = string> {
  title?: string;
  description?: string;
  color?: ThemeColor;
  onConfirm: (close: () => void, phrase?: string) => void | Promise<void>;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  size?: AlertModalSize;
  safePhrases?: Phrase | Phrase[];
}

export function openConfirmPrompt<Phrase extends string = string>({
  title,
  description,
  color,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  size,
  safePhrases: _safePhrases,
}: RequiredOrOptional<ConfirmPromptOptions<Phrase>, "title" | "description">) {
  const phrase = ref("");
  const safePhrases = Array.isArray(_safePhrases) ? _safePhrases : [_safePhrases];

  const disabled = computed(() => {
    if (!_safePhrases) return false;
    return !safePhrases.some((p) => p === phrase.value);
  });

  openAlertDialog(
    <>
      <AlertDialog.Header>
        <AlertDialog.Title>{title}</AlertDialog.Title>
        <AlertDialog.Description>
          {description}
          {_safePhrases && (
            <div class="mt-4">
              <p>
                {$t("dialogs.type_to_confirm_prefix")}{" "}
                {safePhrases
                  .map((p, index) => (
                    <span key={index} class="text-strong bg-gray-2 px-1 py-0.5 italic">
                      {p}
                    </span>
                  ))
                  .reduce<any[]>(
                    (prev, curr, index) => [
                      prev,
                      <span v-show={index} class="text-muted-foreground px-1">
                        {$t("or")}
                      </span>,
                      curr,
                    ],
                    []
                  )}{" "}
                {$t("dialogs.type_to_confirm_suffix")}
              </p>
              <Input v-model={phrase.value} v-focus class="mt-2" />
            </div>
          )}
        </AlertDialog.Description>
      </AlertDialog.Header>
      <AlertDialog.Footer>
        <Button
          label={cancelLabel ?? $t("cancel")}
          variant="secondary"
          color={color}
          type="button"
          onClick={() => {
            closeAlertDialog();
            onCancel?.();
          }}
        />

        <Button
          type="button"
          label={confirmLabel ?? $t("confirm")}
          color={color}
          variant="filled"
          onClick={() => onConfirm(closeAlertDialog, phrase.value)}
          disabled={disabled.value}
        />
      </AlertDialog.Footer>
    </>,
    {
      size: size ?? "lg",
    }
  );
}

export const openConfirmDelete = (
  onConfirm: (close: () => void) => void | Promise<void>,
  options?: Omit<ConfirmPromptOptions, "onConfirm">
) =>
  openConfirmPrompt({
    title: options?.title ?? $t("dialogs.confirm_delete_title", { count: 1 }),
    description: options?.description ?? $t("dialogs.confirm_delete_description"),
    color: options?.color ?? "red",
    onConfirm,
  });

export const openSafeDelete = <T extends string>(
  onConfirm: (close: () => void, phrase?: T) => void | Promise<void>,
  options?: Omit<ConfirmPromptOptions<T>, "onConfirm">
) =>
  openConfirmPrompt({
    title: options?.title ?? $t("dialogs.confirm_delete_title", { count: 1 }),
    description: options?.description ?? $t("dialogs.confirm_delete_description"),
    color: options?.color ?? "red",
    onConfirm: (close, phrase) => onConfirm(close, phrase as T),
    safePhrases: options?.safePhrases ?? ("permanently delete" as T),
  });
